import {
  LawfulPresenceAttestation,
  PublicHealthExplorerDependentInput,
  Relation,
  Sex,
} from 'src/data';

export interface Applicant {
  relation: Relation;
  age: number;
  sex: Sex;
}

interface ExistingMember {
  id?: string | null;
  relation?: Relation | string | null;
  age?: number | null;
  sex?: Sex | string | null;
  lawfulPresence?: LawfulPresenceAttestation | null;
}

/**
 * Translates a basic applicant into the dependent payload
 */
export function getDependentPayload(applicant: Applicant): PublicHealthExplorerDependentInput {
  return {
    age: applicant.age,
    relation: applicant.relation,
    sex: applicant.sex,
    isApplying: true,
    aptcEligible: true,
    hasMec: false,
    isParent: false,
    isSmoker: false,
  };
}

export interface PlansQueryGate {
  sessionID?: string;
  awaitingDefaultExchange: boolean;
  awaitingParams?: boolean;
}

/**
 * Whether the plans queries may run. Shared by the queries' `skip` and the
 * refetch effect: Apollo's `refetch()` ignores `skip`, so an unguarded refetch
 * on mount sends `PublicHealthPlans` with an empty session id (E-8789).
 * `awaitingParams` holds them while the URL params are applied, so plans never
 * render for the old household (E-8633).
 */
export function canQueryPlans({
  sessionID,
  awaitingDefaultExchange,
  awaitingParams = false,
}: PlansQueryGate): boolean {
  return !!sessionID && !awaitingDefaultExchange && !awaitingParams;
}

/**
 * Pairs each applicant with the existing member it replaces:
 * SELF to SELF, SPOUSE to SPOUSE, and children positionally among existing children
 */
export function matchExistingMembers<T extends ExistingMember>(
  applicants: Array<{ relation: Relation | string }>,
  existing: ReadonlyArray<T | null | undefined> = [],
): Array<T | undefined> {
  const pools: Record<string, T[]> = {};

  existing.forEach((m) => {
    if (!m?.relation) return;
    pools[m.relation] = [...(pools[m.relation] || []), m];
  });

  return applicants.map((a) => pools[a.relation]?.shift());
}

/**
 * Builds the full member list for replacePublicHealthExplorerDependents.
 * Replace resets any field we don't send, so lawfulPresence is carried over from the
 * matching existing member unless the applicant sets its own. Ids are never sent.
 */
export function getReplacePayload(
  applicants: Array<Applicant & { lawfulPresence?: LawfulPresenceAttestation | null }>,
  existing: ReadonlyArray<ExistingMember | null | undefined> = [],
): Array<PublicHealthExplorerDependentInput> {
  const matches = matchExistingMembers(applicants, existing);

  return applicants.map((a, i) => {
    const lawfulPresence = a.lawfulPresence ?? matches[i]?.lawfulPresence ?? undefined;

    return {
      ...getDependentPayload(a),
      ...(lawfulPresence ? { lawfulPresence } : {}),
    };
  });
}

const memberKey = (m: ExistingMember | Applicant) => `${m.relation}-${m.age}-${m.sex}`;

/**
 * Whether the household/location params in the URL differ from the stored session.
 * Params that are absent (or an empty applicants list) never count as a difference.
 */
export function paramsDiffer(
  params: {
    zip?: string | null;
    income?: number | null;
    year?: number | null;
    applicants?: Applicant[] | null;
  },
  session: {
    zipcode?: string | null;
    income?: number | null;
    coverageYear?: number | null;
    members?: ReadonlyArray<ExistingMember | null | undefined> | null;
  },
): boolean {
  if (params.zip && params.zip !== session.zipcode) return true;
  if (params.income != null && params.income !== session.income) return true;
  if (params.year && params.year !== session.coverageYear) return true;

  if (params.applicants?.length) {
    const wanted = params.applicants.map(memberKey).sort();
    const current = (session.members || [])
      .filter((m): m is ExistingMember => !!m)
      .map(memberKey)
      .sort();

    if (wanted.length !== current.length) return true;
    if (wanted.some((key, i) => key !== current[i])) return true;
  }

  return false;
}
